import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IDeliveriesState } from './deliveries-state';
import { StateSlice } from '../state-slice';
import { DeliveryOrderEventStatus } from '@core/models/delivery/delivery-order-event-status';
import { FORMAT } from '@core/utils/formats';
import { IDeliveryOrdersFilterState } from './filters/deliveries-filters.state';
import { getDeliveryOrderConfigEntities, getDeliveryOrderEntities, getDispatchConfigEntities } from '../shared/entities/entities-selectors';
import { DeliveryOrder } from '@core/models/delivery/delivery-order';
import { CreateDeliveryOrderRequest } from '@core/models/delivery/create-delivery-order-request';
import { removeObjectKey } from '@core/utils/object';
import { SearchDeliveryOrdersRequest } from '@core/models/delivery/search-delivery-orders-request';
import { DELIVERY_ORDERS_COLUMNS } from '@core/models/delivery/list/delivery-orders-columns';
import { DeliveryOrdersFilters } from '@core/models/delivery/filters/delivery-orders-filters';
import { SearchFilterItem } from '@core/models/common/search-filter-item';
import { Location } from '@core/models/location/location';
import { sortAlphabeticalByProperty } from '@core/utils/sorting';
import { DeliveryOrderEvent } from '@core/models/delivery/delivery-order-event';
import { DeliveryOrderEventType } from '@core/models/delivery/delivery-order-event-type';
import { MOMENT_UNIT } from '@core/utils/moment';
import { DeliveryOrderStatus } from '@core/models/delivery/delivery-order-status';
import { DeliveryOrderQuote } from '@core/models/delivery/quote/delivery-order-quote';
import { DeliveryOrderGroupTracking } from '@core/models/delivery/tracking/delivery-order-group-tracking';
import { DeliveryOrderDriver } from '@core/models/delivery/driver/delivery-order-driver';
import { Moment } from 'moment';
import { DeliveryIntegration } from '@core/models/delivery/integrations/delivery-integration';
import * as moment from 'moment';
import { parseLiveOrderMarkers, parseLiveOrderStatus } from '@core/utils/deliveries';

export const selectDeliveriesState = createFeatureSelector<IDeliveriesState>(StateSlice.DELIVERY);

export const getDeliveryOrdersTracking = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.tracking
);

export const getDeliveryOrdersList = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.orders
);
export const getDeliveryOrderIds = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.orders.ids
);

export const getDeliveryOrdersMeta = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.orders.meta
);

export const getDeliveryOrdersFilterBy = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.orders.filterBy as IDeliveryOrdersFilterState
);
export const getDeliveryOrdersSortBy = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.orders.sortBy
);
export const getDeliveryOrdersSortDirection = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.orders.sortDirection
);

export const isFetching = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.orders.isFetching
);

export const isFetchingMore = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.orders.isFetchingMore
);

export const getDeliveryOrders = createSelector(
    [getDeliveryOrderIds, getDeliveryOrderEntities],
    (orderIds: string[], orders: { [id: string]: DeliveryOrder }) => {
        return orderIds.map((orderId: string) => {
            return Object.assign(new DeliveryOrder(), orders[orderId], {
                refund: !!orders[orderId]?.refund
                    ? {
                          ...orders[orderId].refund,
                          label: orders[orderId].refund.getStatusLabel(),
                      }
                    : null,
            });
        });
    }
);

export const getDeliveryOrderQuotes = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.quotes
);

export const getNewDeliveryOrder = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.newOrder
);

export const getDeliveryOrderQuotesRequestFromNewOrder = createSelector(
    getNewDeliveryOrder,
    (newOrderRequest: CreateDeliveryOrderRequest) => {
        if (newOrderRequest) {
            return {
                location_id: newOrderRequest.locationId,
                order_number: newOrderRequest.orderNumber,
                requires_id: newOrderRequest.requiresId || false,
                requires_dropoff_signature:
                    newOrderRequest.requiresDropoffSignature || false,
                order_type: newOrderRequest.orderType,
                tip: newOrderRequest.tip,
                total: newOrderRequest.total,
                order_items: newOrderRequest.orderItems,
                customer_name: newOrderRequest.customerName,
                customer_phone: newOrderRequest.customerPhone,
                customer_address: newOrderRequest.customerAddress,
                delivery_instruction: newOrderRequest.deliveryInstruction,
                pickup_at_ready: newOrderRequest.pickupAtReady,
                payment_methods: newOrderRequest.paymentMethods
            };
        } else {
            return null;
        }
    }
);

export const getDeliveryOrderConfigs = createSelector(
    [getDeliveryOrderConfigEntities],
    (deliveryOrderConfigs) => deliveryOrderConfigs
);

export const isFetchingDeliveryOrderConfigs = createSelector(
    [selectDeliveriesState],
    (deliveriesState) => deliveriesState.isFetchingDeliveryConfig
)

export const isFetchingDispatchConfig = createSelector(
    [selectDeliveriesState],
    (deliveriesState) => deliveriesState.isFetchingDispatchConfig
);

export const getDispatchConfigs = createSelector(
    [getDispatchConfigEntities],
    (dispatchConfigs) => dispatchConfigs
);

export const getDeliveryOrdersSummary = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.summary
);

export const getSearchQuery = createSelector(
    [getDeliveryOrdersFilterBy, getDeliveryOrdersSortBy, getDeliveryOrdersSortDirection],
    (filterBy, sortBy, sortDirection) => {
        const searchRequest: SearchDeliveryOrdersRequest = new SearchDeliveryOrdersRequest();

        searchRequest.filters = removeObjectKey('domain', filterBy);
        searchRequest.sort_by = [];

        const sortByKeys: string[] = (sortBy as string).split(':');
        // sortByKeys.forEach((sortByKey, index) => {
        //     const filteredSortDirection: string = isArray(sortDirection) ? sortDirection[index] : sortDirection;
        //     searchRequest.sort_by.push(`${sortByKey}:${filteredSortDirection}`);
        // });

        return searchRequest;
    }
);

export const getColumns = (_: IDeliveriesState) => DELIVERY_ORDERS_COLUMNS;

export const getFilters = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.filters
);

export const getViewedDeliveryOrderId = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.viewedOrderId
);

export const getFilterLocations = createSelector(
    [getFilters],
    (filters: DeliveryOrdersFilters) => {
        const locations: SearchFilterItem[] = [];

        if (filters?.locations) {
            const sortedLocations: Location[] = ([...filters.locations] as { [id: string]: any }[]).sort(
                sortAlphabeticalByProperty('name')
            ).map((location) => location as Location);

            sortedLocations.forEach((location: Location) => {
                locations.push({
                    id: location.id,
                    label: location.name,
                });
            });
        }

        return locations;
    }
);

export const isFilterApplied = createSelector(
    [getDeliveryOrdersFilterBy],
    (filters: IDeliveryOrdersFilterState) => {
        let isApplied: boolean = false;

        if (filters) {
            isApplied =
                !!filters.locations?.length ||
                !!filters.status?.length;
        }

        return isApplied;
    }
);

export const getViewedDeliveryOrder = createSelector(
    [getViewedDeliveryOrderId, getDeliveryOrderEntities],
    (orderId: string, orders: { [id: string]: DeliveryOrder }) => {
        const order: DeliveryOrder = orders[orderId];

        // Build events timeline
        let createdEvent: DeliveryOrderEvent = new DeliveryOrderEvent({
            type: DeliveryOrderEventType.ORDERED,
            status: DeliveryOrderEventStatus.COMPLETE,
        });
        let dropoffEvent;
        if (order) {
            const headerFormat: string = FORMAT.DAY_DATE_MONTH_SHORT;
            if (order.createdAt && order.createdAt?.isValid()) {
                createdEvent.createdAt = order.createdAt;
                createdEvent.title = order.createdAt.format(
                    FORMAT.TIME_MERIDIAN
                );
                createdEvent.subtitle = 'Order.Timeline.Ordered';
                createdEvent.header = order.createdAt.format(headerFormat);
            }

            let pickupEvent: DeliveryOrderEvent = new DeliveryOrderEvent({
                type: DeliveryOrderEventType.PICKUP,
                status: DeliveryOrderEventStatus.INACTIVE,
            });
            if (order.pickupAt && order.pickupAt?.isValid()) {
                pickupEvent.createdAt = order.pickupAt;
                pickupEvent.latLng = order.pickup.location;
                pickupEvent.title = order.pickupAt.format(FORMAT.TIME_MERIDIAN);
                pickupEvent.subtitle = 'Order.Timeline.PickUpAt';
                if (!order.createdAt.isSame(order.pickupAt, MOMENT_UNIT.DAYS)) {
                    // if on a different date to created then show date in the header
                    pickupEvent.header = order.pickupAt.format(headerFormat);
                }
            }
            dropoffEvent = new DeliveryOrderEvent({
                type: DeliveryOrderEventType.DELIVERY,
                status: DeliveryOrderEventStatus.INACTIVE,
                latLng: order.dropoff?.location,
            });
            if (order.dropoffAt && order.dropoffAt?.isValid()) {
                dropoffEvent.createdAt = order.dropoffAt;
                dropoffEvent.title = order.dropoffAt.format(
                    FORMAT.TIME_MERIDIAN
                );

                if (
                    !order.createdAt.isSame(order.dropoffAt, MOMENT_UNIT.DAYS)
                ) {
                    // if on a different date to created then show date in the header
                    dropoffEvent.header = order.dropoffAt.format(headerFormat);
                }
            }

            if (order.status === DeliveryOrderStatus.NEW) {
                createdEvent.status = DeliveryOrderEventStatus.COMPLETE;
                createdEvent.createdAt = order.createdAt;
                createdEvent.title = order.createdAt?.format(
                    FORMAT.TIME_MERIDIAN
                );
                createdEvent.subtitle = 'Order.Timeline.Ordered';

                pickupEvent.status = DeliveryOrderEventStatus.INACTIVE;
                pickupEvent.createdAt = order.pickupAt;
                pickupEvent.latLng = order.pickup.location;
                formatPickupAt(order, pickupEvent, order.pickupAtEta);

                dropoffEvent.status = DeliveryOrderEventStatus.ACTIVE;
                dropoffEvent.createdAt = order.dropoffAt;
                dropoffEvent.latLng = order.dropoff.location;
                formatDropoffAt(order, dropoffEvent);

            } else if (order.status === DeliveryOrderStatus.SCHEDULED) {
                createdEvent.status = DeliveryOrderEventStatus.COMPLETE;
                createdEvent.createdAt = order.createdAt;
                createdEvent.title = order.createdAt?.format(
                    FORMAT.TIME_MERIDIAN
                );
                createdEvent.subtitle = 'Order.Timeline.Ordered';

                pickupEvent.status = DeliveryOrderEventStatus.ACTIVE;
                pickupEvent.createdAt = order.pickupAt;
                pickupEvent.latLng = order.pickup.location;
                pickupEvent.header = order.pickupAtReady.isSame(
                    order.createdAt,
                    MOMENT_UNIT.DAYS
                )
                    ? ''
                    : order.pickupAtReady.format(headerFormat);
                pickupEvent.subtitle = 'Order.Timeline.ScheduledPickup';
                pickupEvent.title = order.pickupAtReady.format('h:mm A');

                dropoffEvent.status = DeliveryOrderEventStatus.ACTIVE;
                dropoffEvent.createdAt = order.dropoffAtEta;
                dropoffEvent.latLng = order.dropoff.location;

                if (order.dropoffAtEta && order.dropoffAtEta.isValid()) {
                    dropoffEvent.header = order.dropoffAtEta.isSame(
                        order.createdAt,
                        MOMENT_UNIT.DAYS
                    )
                        ? ''
                        : order.dropoffAtEta.format(headerFormat);
                    dropoffEvent.subtitle = 'Order.Timeline.ScheduledDropoff';
                    dropoffEvent.title = order.dropoffAtEta.format('h:mm A');
                }
            } else if (
                order.status === DeliveryOrderStatus.PICKUP ||
                order.status === DeliveryOrderStatus.ACCEPTED ||
                order.status === DeliveryOrderStatus.ASSIGNED
            ) {
                createdEvent.status = DeliveryOrderEventStatus.COMPLETE;
                createdEvent.title = order.createdAt?.format(
                    FORMAT.TIME_MERIDIAN
                );

                pickupEvent.status = DeliveryOrderEventStatus.ACTIVE;
                pickupEvent.createdAt = order.pickupAt;
                pickupEvent.latLng = order.pickup.location;
                formatPickupAt(order, pickupEvent, order.pickupAtEta);

                dropoffEvent.status = DeliveryOrderEventStatus.ACTIVE;
                dropoffEvent.createdAt = order.dropoffAt;
                dropoffEvent.latLng = order.dropoff.location;
                formatDropoffAt(order, dropoffEvent);

            } else if (order.status === DeliveryOrderStatus.DROPOFF) {
                pickupEvent.status = DeliveryOrderEventStatus.COMPLETE;
                pickupEvent.createdAt = order.pickupAt;
                pickupEvent.latLng = order.pickup.location;
                pickupEvent.title = order.pickupAt.format(FORMAT.TIME_MERIDIAN);
                pickupEvent.subtitle = 'Order.Timeline.PickUp';

                dropoffEvent.status = DeliveryOrderEventStatus.ACTIVE;
                dropoffEvent.createdAt = order.dropoffAt;
                dropoffEvent.latLng = order.dropoff.location;
                formatDropoffAt(order, dropoffEvent);

            } else if (order.status === DeliveryOrderStatus.DELIVERED) {
                pickupEvent.status = DeliveryOrderEventStatus.COMPLETE;
                pickupEvent.subtitle = 'Order.Timeline.PickUp';

                dropoffEvent.status = DeliveryOrderEventStatus.COMPLETE;
                dropoffEvent.latLng = order.dropoff.location;
                dropoffEvent.title = order.dropoffAt.format(
                    FORMAT.TIME_MERIDIAN
                );
                dropoffEvent.subtitle = 'Order.Timeline.Delivered';

            } else if (order.status === DeliveryOrderStatus.REJECTED) {
                createdEvent.isLastEvent = true;

                pickupEvent.title = '';
                pickupEvent.subtitle = '';

            } else if (order.status === DeliveryOrderStatus.CANCELLED) {
                const orderWasPickedUp: boolean =
                    order.pickupAt?.isValid && order.pickupAt.isValid();

                createdEvent.isLastEvent = !orderWasPickedUp;

                if (orderWasPickedUp) {
                    pickupEvent.status = DeliveryOrderEventStatus.COMPLETE;
                    pickupEvent.isLastEvent = orderWasPickedUp;
                    pickupEvent.createdAt = order.pickupAt;
                    pickupEvent.latLng = order.pickup.location;
                    formatPickupAt(order, pickupEvent, order.pickupAt, false);
                } else {
                    pickupEvent.status = DeliveryOrderEventStatus.INACTIVE;
                    pickupEvent.title = '';
                    pickupEvent.subtitle = '';
                }

                dropoffEvent.title = '';
                dropoffEvent.subtitle = '';
            } else if (order.status === DeliveryOrderStatus.RETURNED) {
                pickupEvent.status = DeliveryOrderEventStatus.COMPLETE;
                pickupEvent.createdAt = order.pickupAt;
                pickupEvent.latLng = order.pickup.location;
                pickupEvent.title = order.pickupAt.format(FORMAT.TIME_MERIDIAN);
                pickupEvent.subtitle = 'Order.Timeline.PickUp';
                pickupEvent.isLastEvent = true;

                dropoffEvent.title = '';
                dropoffEvent.subtitle = '';
            } else if (order.status === DeliveryOrderStatus.RETURNING) {
                pickupEvent.status = DeliveryOrderEventStatus.COMPLETE;
                pickupEvent.createdAt = order.pickupAt;
                pickupEvent.latLng = order.pickup.location;
                pickupEvent.title = order.pickupAt.format(FORMAT.TIME_MERIDIAN);
                pickupEvent.subtitle = 'Order.Timeline.PickUp';
                pickupEvent.isLastEvent = true;
            }

            order.events = [createdEvent, pickupEvent, dropoffEvent];

            if (order.pickupAtReady?.isValid && order.pickupAtReady.isValid()) {
                
                let description = '';
                let params = { time: null, date: null };
                const now = moment();

                if (order.pickupAtReady.isBefore(now)) {

                    if (order.pickupAtReady.isSame(now, MOMENT_UNIT.DAYS)) {

                        description = 'Order.Details.PickupAtReady.PastAndToday';
                        params.time = order.pickupAtReady.format(FORMAT.TIME_SHORT_WITH_MERIDIAN);
                    } else {
                        description = 'Order.Details.PickupAtReady.PastAndNotToday';
                        params.date = order.pickupAtReady.format(FORMAT.FULL_MONTH_AND_DATE);
                        params.time = order.pickupAtReady.format(FORMAT.TIME_SHORT_WITH_MERIDIAN);
                    }
                } else {
                    if (order.pickupAtReady.isSame(now, MOMENT_UNIT.DAYS)) {

                        description = 'Order.Details.PickupAtReady.FutureAndToday';
                        params.time = order.pickupAtReady.format(FORMAT.TIME_SHORT_WITH_MERIDIAN);

                    } else {
                        description = 'Order.Details.PickupAtReady.FutureAndNotToday';
                        params.date = order.pickupAtReady.format(FORMAT.FULL_MONTH_AND_DATE);
                        params.time = order.pickupAtReady.format(FORMAT.TIME_SHORT_WITH_MERIDIAN);
                    }
                }

                order.pickupAtReadyDescription = description;
                order.pickupAtReadyDescriptionParams = params;
            }
        }

        return order;
    }
);

export const getViewedOrderQuotes = createSelector(
    [getNewDeliveryOrder, getViewedDeliveryOrderId, getDeliveryOrderQuotes],
    (
        newOrder: CreateDeliveryOrderRequest,
        orderId: string,
        quotes: { [id: string]: DeliveryOrderQuote[] }
    ) => {
        return quotes[newOrder?.orderNumber] || quotes[orderId];
    }
);

export const isFetchingOrderQuotes = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.isFetchingQuotes
);

export const getDeliveriesGroupTracking = createSelector(
    getDeliveryOrdersTracking,
    (tracking: DeliveryOrderGroupTracking) => {
        return !!tracking
            ? new DeliveryOrderGroupTracking({
                  ...tracking,
                  drivers: (tracking?.drivers || []).map(
                      (driver: DeliveryOrderDriver) => {
                          const processedDriver: DeliveryOrderDriver = new DeliveryOrderDriver(
                              driver
                          );

                          let driverOrders: { [key: string]: string } = {};

                          (tracking.orders || [])
                              .filter(
                                  (order: DeliveryOrder) =>
                                      order?.driver?.id === processedDriver.id
                              )
                              .forEach(
                                  (order: DeliveryOrder) =>
                                      (driverOrders[order.id] = order.reference)
                              );

                          processedDriver.orders = driverOrders;

                          return processedDriver;
                      }
                  ),
              })
            : null;
    }
);

export const getDeliveryIntegrationEntities = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.integrations
);

export const getDeliveryIntegrations = createSelector(
    getDeliveryIntegrationEntities,
    (integrationsEntities: { [provider: string]: DeliveryIntegration }) => {
        const keys: string[] = Object.keys(integrationsEntities);

        return keys.map((key: string) => integrationsEntities[key]);
    }
)

export const isFetchingDeliveryIntegrations = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.isFetchingIntegrations
);

export const getViewedDeliveryIntegrationId = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.viewedDeliveryIngrationId
)

export const getViewedDeliveryIntegration = createSelector(
    [getDeliveryIntegrationEntities, getViewedDeliveryIntegrationId],
    (integrationsEntities: { [provider: string]: DeliveryIntegration }, provider: string) => {
        return !!integrationsEntities[provider] ? integrationsEntities[provider] : null;
    }
);

export const getLiveDeliveryOrders = createSelector(
    [getDeliveryOrderIds, getDeliveryOrderEntities],
    (orderIds: string[], orders: { [id: string]: DeliveryOrder }) => {
        return orderIds.
        filter((orderId: string) => { return !!orders[orderId]}).
        map((orderId: string) => {
            return Object.assign(new DeliveryOrder(), orders[orderId], {
                statusLabel: parseLiveOrderStatus(orders[orderId].status),
                refund: !!orders[orderId].refund
                    ? {
                          ...orders[orderId].refund,
                          label: orders[orderId].refund.getStatusLabel(),
                      }
                    : null,
            });
        });
    }
);

export const getLiveEnrouteDeliveryOrders = createSelector(
    [getLiveDeliveryOrders],
    (orders: DeliveryOrder[]) => {

        // map for efficient lookup of sortIndex
        const statusIndex = {
            [DeliveryOrderStatus.PICKUP]: 0,
            [DeliveryOrderStatus.DROPOFF]: 1,
            [DeliveryOrderStatus.RETURNING]: 2
        };

        return orders.filter((order: DeliveryOrder) => 
            order.status === DeliveryOrderStatus.DROPOFF ||
            order.status === DeliveryOrderStatus.PICKUP ||
            order.status === DeliveryOrderStatus.RETURNING
        ).sort((orderA, orderB) => statusIndex[orderA.status] - statusIndex[orderB.status] || 
            moment(orderB.createdAt).diff(moment(orderA.createdAt), MOMENT_UNIT.MINUTES));
    }
);

export const getLiveDispatchingDeliveryOrders = createSelector(
    [getLiveDeliveryOrders],
    (orders: DeliveryOrder[]) => {

        // map for efficient lookup of sortIndex
        const statusIndex = {
            [DeliveryOrderStatus.REJECTED]: 0,
            [DeliveryOrderStatus.NEW]: 1,
            [DeliveryOrderStatus.ACCEPTED]: 2,
            [DeliveryOrderStatus.ASSIGNED]: 3
        };

        return orders.filter((order: DeliveryOrder) => 
            order.status === DeliveryOrderStatus.ACCEPTED ||
            order.status === DeliveryOrderStatus.NEW ||
            order.status === DeliveryOrderStatus.REJECTED ||
            order.status === DeliveryOrderStatus.ASSIGNED
        ).sort((orderA, orderB) => statusIndex[orderA.status] - statusIndex[orderB.status] || 
            moment(orderB.createdAt).diff(moment(orderA.createdAt), MOMENT_UNIT.MINUTES));
    }
);

export const getLiveRecentDeliveryOrders = createSelector(
    [getLiveDeliveryOrders],
    (orders: DeliveryOrder[]) => {

        // map for efficient lookup of sortIndex
        const statusIndex = {
            [DeliveryOrderStatus.DELIVERED]: 0,
            [DeliveryOrderStatus.CANCELLED]: 1,
            [DeliveryOrderStatus.RETURNED]: 2
        };

        return orders.filter((order: DeliveryOrder) => 
            order.status === DeliveryOrderStatus.DELIVERED ||
            order.status === DeliveryOrderStatus.CANCELLED ||
            order.status === DeliveryOrderStatus.RETURNED
        ).sort((orderA, orderB) => statusIndex[orderA.status] - statusIndex[orderB.status] || 
            moment(orderB.createdAt).diff(moment(orderA.createdAt), MOMENT_UNIT.MINUTES));
    }
);

export const isDeliveryOrderOnFocus = createSelector(
    selectDeliveriesState,
    (state: IDeliveriesState) => state.isOrderOnFocus
);

export const getLiveViewMapMarkers = createSelector(
    [getDeliveryOrders, getViewedDeliveryOrder],
    (orders: DeliveryOrder[], viewedOrder: DeliveryOrder ) => {
        let markers = [];

        if (!!viewedOrder) {
            markers.push(...parseLiveOrderMarkers(viewedOrder, true));

        } else {
            orders.forEach((order: DeliveryOrder) => {
                markers.push(...parseLiveOrderMarkers(order, false));
            });
        }

        return markers;
    }
);

export function formatPickupAt(
    order: DeliveryOrder,
    orderEvent: DeliveryOrderEvent,
    pickupTarget: Moment,
    inProgress: boolean = true
) {
    const createdAt: moment.Moment = order.createdAt;
    const headerFormat: string = FORMAT.DAY_DATE_MONTH_SHORT;

    if (order.pickupAt && order.pickupAt.isValid()) {
        if (!createdAt.isSame(order.pickupAt, MOMENT_UNIT.DAYS)) {
            orderEvent.header = order.pickupAt.format(headerFormat);
        }

        orderEvent.title = order.pickupAt.format(FORMAT.TIME_MERIDIAN);
        orderEvent.subtitle = 'Order.Timeline.PickUpAt';
        const timeRemaining: number = order.pickupAt.diff(
            pickupTarget,
            MOMENT_UNIT.MINUTES
        );
        const timeAgo: number = createdAt.diff(
            order.pickupAt,
            MOMENT_UNIT.MINUTES
        );

        let dropoffLabel: string = `${Math.abs(timeRemaining)}`;

        if (inProgress) {
            if (timeRemaining === 0) {
                orderEvent.subtitle = 'Order.Timeline.DueNow';
            } else if (timeRemaining < 0) {
                orderEvent.subtitle = 'Order.Timeline.PickUpAgo';
            }

            orderEvent.time = dropoffLabel;
        } else {
            orderEvent.subtitle = 'Order.Timeline.PickUp';
        }
    } else if (order.pickupAtEta && order.pickupAtEta.isValid()) {
        if (!createdAt.isSame(order.pickupAtEta, MOMENT_UNIT.DAYS)) {
            orderEvent.header = order.pickupAtEta.format(headerFormat);
        }

        orderEvent.title = order.pickupAtEta.format(FORMAT.TIME_MERIDIAN);
        orderEvent.subtitle =
            order.status === DeliveryOrderStatus.SCHEDULED
                ? 'Order.Timeline.ScheduledPickup'
                : 'Order.Timeline.PickupETA';
    }
}

export function formatDropoffAt(order: DeliveryOrder, orderEvent: DeliveryOrderEvent) {
    const createdAt: moment.Moment = order.createdAt;
    const headerFormat: string = FORMAT.DAY_DATE_MONTH_SHORT;

    if (order.dropoffAt && order.dropoffAt.isValid()) {
        if (!createdAt.isSame(order.dropoffAt, MOMENT_UNIT.DAYS)) {
            orderEvent.header = order.dropoffAt.format(headerFormat);
        }

        orderEvent.title = order.dropoffAt.format(FORMAT.TIME_MERIDIAN);
        orderEvent.subtitle = 'Order.Timeline.DeliveryAt';
        const timeRemaining: number = order.dropoffAtEta.diff(
            createdAt,
            MOMENT_UNIT.MINUTES
        );
        let dropoffLabel: string = `${Math.abs(timeRemaining)}`;
        if (timeRemaining < 0) {
            orderEvent.subtitle = 'Order.Timeline.DeliveryAgo';
        }
        if (timeRemaining === 0) {
            orderEvent.subtitle = 'Order.Timeline.DueNow';
        }
        orderEvent.time = dropoffLabel;
    } else if (order.dropoffAtEta && order.dropoffAtEta.isValid()) {
        if (!createdAt.isSame(order.dropoffAtEta, MOMENT_UNIT.DAYS)) {
            orderEvent.header = order.dropoffAtEta.format(headerFormat);
        }

        orderEvent.title = order.dropoffAtEta.format(FORMAT.TIME_MERIDIAN);
        orderEvent.subtitle = 'Order.Timeline.DropoffETA';
    }
}